import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { MapPin, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Venue = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-20 bg-[#EFF4F9]" data-testid="venue-section">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }} className="text-center mb-12">
          <h2 className="font-serif text-4xl sm:text-5xl text-[#1A2B3D] mb-4">{t('venueTitle')}</h2>
          <div className="wave-divider" />
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }} className="rounded-sm overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1678459505864-3ad3593d97d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxBbm5hcG9saXMlMjBDaGVzYXBlYWtlJTIwQmF5JTIwc2FpbGJvYXRzJTIwaGFyYm9yfGVufDB8fHx8MTc4NzYxNzAyM3ww&ixlib=rb-4.1.0&q=85"
              alt="Annapolis and the Chesapeake Bay"
              className="w-full h-auto" />
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}>
            <h3 className="font-serif text-2xl text-[#1A2B3D] mb-4">{t('venueSubtitle')}</h3>
            <p className="text-[#6B8299] leading-relaxed mb-4">{t('venueDesc')}</p>
            <p className="text-[#6B8299] leading-relaxed mb-6 text-sm">{t('venueHistory')}</p>

            <div className="border-t border-[#CBD5E1] pt-6">
              <div className="flex items-start gap-3 mb-4">
                <MapPin className="w-5 h-5 text-[#C9A96E] mt-0.5" />
                <div>
                  <p className="text-[#1A2B3D] font-medium text-sm">Annapolis, Maryland</p>
                  <p className="text-[#6B8299] text-sm">{t('venueAddress')}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <a href="https://www.visitannapolis.org" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#1E3A5F] text-sm hover:text-[#C9A96E] transition-colors"
                  data-testid="venue-website-link">
                  {t('venueWebsite')} <ExternalLink className="w-3 h-3" />
                </a>
                <a href="https://maps.google.com/?q=1195+Mace+Road+Annapolis+MD" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#1E3A5F] text-sm hover:text-[#C9A96E] transition-colors"
                  data-testid="venue-map-link">
                  {t('googleMaps')} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }} className="mt-10 rounded-sm overflow-hidden border border-[#CBD5E1]">
          <iframe
            title="Venue location - 1195 Mace Road, Annapolis, MD"
            src="https://maps.google.com/maps?q=1195+Mace+Road,+Annapolis,+MD+21403&output=embed"
            className="w-full"
            style={{ height: '350px', border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="venue-map-embed"
          />
        </motion.div>
      </div>
    </section>
  );
};
